/**
 * WORKSPACE SETTINGS TAB
 *
 * General workspace settings (name, description, color, icon) and
 * danger zone (delete workspace with type-to-confirm).
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { EmojiIconPicker } from "@/components/EmojiIconPicker";
import { WORKSPACE_COLORS } from "@/lib/workspace-constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { AlertTriangle, Loader2, Check } from "lucide-react";

interface WorkspaceSettingsTabProps {
  workspaceId: string;
  onCloseSettings: () => void;
}

export function WorkspaceSettingsTab({ workspaceId, onCloseSettings }: WorkspaceSettingsTabProps) {
  const { workspaces, updateWorkspace, removeWorkspace } = useWorkspaceStore();
  const workspace = workspaces.find((w) => w.id === workspaceId);

  const [name, setName] = useState(workspace?.name ?? "");
  const [description, setDescription] = useState(workspace?.description ?? "");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [savedField, setSavedField] = useState<string | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); }, []);

  const showSaved = useCallback((field: string) => {
    setSavedField(field);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => setSavedField(null), 1500);
  }, []);

  if (!workspace) return null;

  const handleNameBlur = async () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== workspace.name) {
      const ok = await updateWorkspace(workspaceId, { name: trimmed });
      if (ok) showSaved("name");
    }
  };

  const handleDescriptionBlur = async () => {
    if (description !== workspace.description) {
      const ok = await updateWorkspace(workspaceId, { description });
      if (ok) showSaved("description");
    }
  };

  const handleColorChange = async (color: string) => {
    const ok = await updateWorkspace(workspaceId, { color });
    if (ok) showSaved("color");
  };

  const handleIconChange = async (icon: string) => {
    const ok = await updateWorkspace(workspaceId, { icon });
    if (ok) showSaved("icon");
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const success = await removeWorkspace(workspaceId);
      if (success) {
        setDeleteDialogOpen(false);
        onCloseSettings();
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const canDelete = deleteConfirmText === workspace.name;

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-base font-semibold">Workspace</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your workspace settings and appearance.
        </p>
      </div>

      <div className="flex items-start gap-4">
        <div className="pt-6">
          <EmojiIconPicker
            value={workspace.icon ?? "colony"}
            color={workspace.color}
            onChange={handleIconChange}
          />
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="ws-settings-name" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Name
            </Label>
            {savedField === "name" && <SavedBadge />}
          </div>
          <Input
            id="ws-settings-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            maxLength={100}
            className="h-9"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="ws-settings-desc" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Description
          </Label>
          {savedField === "description" && <SavedBadge />}
        </div>
        <Textarea
          id="ws-settings-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={handleDescriptionBlur}
          placeholder="What's this workspace for?"
          rows={3}
          className="resize-none"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Color
          </Label>
          {savedField === "color" && <SavedBadge />}
        </div>
        <div className="flex gap-2 flex-wrap">
          {WORKSPACE_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => handleColorChange(c)}
              className={cn(
                "w-7 h-7 rounded-full transition-all duration-150 ring-offset-background",
                workspace.color === c
                  ? "scale-110 ring-2 ring-offset-2 ring-white/70"
                  : "hover:scale-110 opacity-70 hover:opacity-100"
              )}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      </div>

      <div className="border border-destructive/30 rounded-lg p-4 mt-6">
        <h4 className="text-sm font-semibold text-destructive flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Danger Zone
        </h4>
        <p className="text-sm text-muted-foreground mt-2">
          Permanently delete this workspace and all its projects, tasks, and data.
          This action cannot be undone.
        </p>
        <Button
          variant="destructive"
          size="sm"
          className="mt-3"
          onClick={() => { setDeleteConfirmText(""); setDeleteDialogOpen(true); }}
        >
          Delete Workspace
        </Button>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Workspace</DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{workspace.name}</strong> and all
              its projects, tasks, members, and data. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="delete-confirm" className="text-sm">
              Type <strong>{workspace.name}</strong> to confirm
            </Label>
            <Input
              id="delete-confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={workspace.name}
              className="h-9"
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!canDelete || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Workspace"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SavedBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-500 animate-in fade-in duration-200">
      <Check className="w-3 h-3" />
      Saved
    </span>
  );
}
